"use client";

import {
  isSensitiveAnalyticsPath,
  POSTHOG_API_HOST,
  POSTHOG_SESSION_RECORDING,
  redirectsToSensitivePath,
} from "@/lib/analytics/constants";
import { dropInjectedScriptExceptions } from "@/lib/analytics/drop-injected-script-exceptions";
import { sanitizePostHogProperties } from "@/lib/analytics/scrub";
import { isProduction } from "@/utils/env-utils";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { initializeAgGridLicense } from "@/lib/ag-grid/license";

/**
 * Whether the page PostHog initializes on is a sensitive route, or a sign-in that leads to one.
 *
 * @returns True if autocapture and recording must start off.
 */
function isSensitiveLandingPage(): boolean {
  return isSensitiveAnalyticsPath(window.location.pathname) || redirectsToSensitivePath(window.location.search);
}

if (typeof window !== "undefined" && isProduction) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    api_host: POSTHOG_API_HOST,
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    // Autocapture, session recording, and $set_once read the location straight off the page;
    // this scrubs the forms edit token and sensitive-path tokens out of every location property.
    sanitize_properties: sanitizePostHogProperties,
    // The recorder and autocapture start here, before PostHogPageView can stop them.
    ...(isSensitiveLandingPage() && { autocapture: false, disable_session_recording: true }),
    // Snapchat, Instagram and Brave on iOS inject scripts that throw against their own native
    // bridges; autocapture files those under our pages as if they were our bugs.
    before_send: dropInjectedScriptExceptions,
    session_recording: POSTHOG_SESSION_RECORDING,
  });
}

// Initialize AG Grid license
if (typeof window !== "undefined") {
  initializeAgGridLicense();
}

interface PHProviderProps extends React.PropsWithChildren {
  siteUrl: string;
}

/**
 * Wraps the app in the PostHog provider, but only in production — elsewhere the children
 * render untouched so local and preview runs emit no analytics.
 *
 * @param children - The subtree to render inside the provider.
 * @param siteUrl - Public site URL for the current deployment.
 * @returns The children, wrapped in `PostHogProvider` when running in production.
 */
export function PHProvider({ children, siteUrl }: PHProviderProps): JSX.Element {
  // Only use PostHog in production
  if (isProduction) {
    return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
  }
  return <>{children}</>;
}
